import React, { useState, useEffect, useCallback } from 'react';
import { 
  Lock, Unlock, Eye, EyeOff, Copy, Download, Upload, RotateCcw, 
  Settings, Info, Key, Shield, Hash, Moon, Sun, Shuffle, 
  AlertCircle, CheckCircle, Zap, FileText, History
} from 'lucide-react';

interface CipherMethod {
  id: string;
  name: string;
  description: string;
  keyRequired: boolean;
  keyType: 'text' | 'numeric' | 'none';
  securityLevel: 'low' | 'medium' | 'high';
}

interface EncryptionHistory {
  id: string;
  method: string;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  success: boolean;
  inputLength: number;
  outputLength: number;
}

const TextEncryptDecryptApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [encryptionKey, setEncryptionKey] = useState<string>('');
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  const [selectedMethod, setSelectedMethod] = useState<string>('caesar');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showKey, setShowKey] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'info' | 'history' | 'settings' | null>(null);
  
  // Advanced options
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [preserveSpaces, setPreserveSpaces] = useState<boolean>(true);
  const [preservePunctuation, setPreservePunctuation] = useState<boolean>(true);
  const [autoProcess, setAutoProcess] = useState<boolean>(true);
  
  // Analytics
  const [history, setHistory] = useState<EncryptionHistory[]>([]);
  const [lastResult, setLastResult] = useState<{ success: boolean; message: string } | null>(null);

  const cipherMethods: CipherMethod[] = [
    {
      id: 'caesar',
      name: 'Caesar Cipher',
      description: 'Simple shift cipher with numeric key (0-25)',
      keyRequired: true,
      keyType: 'numeric',
      securityLevel: 'low'
    },
    {
      id: 'atbash',
      name: 'Atbash Cipher',
      description: 'Reverses alphabet (A=Z, B=Y, etc.)',
      keyRequired: false,
      keyType: 'none',
      securityLevel: 'low'
    },
    {
      id: 'rot13',
      name: 'ROT13',
      description: 'Caesar cipher with fixed shift of 13',
      keyRequired: false,
      keyType: 'none',
      securityLevel: 'low'
    },
    {
      id: 'vigenere',
      name: 'Vigenère Cipher',
      description: 'Polyalphabetic cipher with text key',
      keyRequired: true,
      keyType: 'text',
      securityLevel: 'medium'
    },
    {
      id: 'simple_substitution',
      name: 'Simple Substitution',
      description: 'Maps each letter to another letter',
      keyRequired: true,
      keyType: 'text',
      securityLevel: 'medium'
    },
    {
      id: 'base64',
      name: 'Base64 Encoding',
      description: 'Standard Base64 encoding/decoding',
      keyRequired: false,
      keyType: 'none',
      securityLevel: 'low'
    },
    {
      id: 'reverse',
      name: 'Reverse Text',
      description: 'Simply reverses the input text',
      keyRequired: false,
      keyType: 'none',
      securityLevel: 'low'
    },
    {
      id: 'morse',
      name: 'Morse Code',
      description: 'International Morse code translation',
      keyRequired: false,
      keyType: 'none',
      securityLevel: 'low'
    }
  ];

  // Caesar cipher implementation
  const caesarCipher = useCallback((text: string, shift: number, encrypt: boolean): string => {
    if (!text) return '';
    
    const actualShift = encrypt ? shift : -shift;
    
    return text.replace(/[a-zA-Z]/g, (char) => {
      const isUpperCase = char === char.toUpperCase();
      const baseCode = isUpperCase ? 65 : 97;
      const charCode = char.charCodeAt(0) - baseCode;
      const shiftedCode = ((charCode + actualShift) + 26) % 26;
      return String.fromCharCode(shiftedCode + baseCode);
    });
  }, []);

  // Atbash cipher implementation
  const atbashCipher = useCallback((text: string): string => {
    if (!text) return '';
    
    return text.replace(/[a-zA-Z]/g, (char) => {
      const isUpperCase = char === char.toUpperCase();
      const baseCode = isUpperCase ? 65 : 97;
      const charCode = char.charCodeAt(0) - baseCode;
      const reversedCode = 25 - charCode;
      return String.fromCharCode(reversedCode + baseCode);
    });
  }, []);

  // Vigenère cipher implementation
  const vigenereCipher = useCallback((text: string, key: string, encrypt: boolean): string => {
    if (!text || !key) return '';
    
    const cleanKey = key.replace(/[^a-zA-Z]/g, '').toUpperCase();
    if (!cleanKey) return text;
    
    let keyIndex = 0;
    
    return text.replace(/[a-zA-Z]/g, (char) => {
      const isUpperCase = char === char.toUpperCase();
      const baseCode = isUpperCase ? 65 : 97;
      const charCode = char.charCodeAt(0) - baseCode;
      const keyChar = cleanKey[keyIndex % cleanKey.length];
      const keyShift = keyChar.charCodeAt(0) - 65;
      
      const shift = encrypt ? keyShift : -keyShift;
      const shiftedCode = ((charCode + shift) + 26) % 26;
      
      keyIndex++;
      return String.fromCharCode(shiftedCode + baseCode);
    });
  }, []);

  // Simple substitution cipher
  const substitutionCipher = useCallback((text: string, key: string, encrypt: boolean): string => {
    if (!text) return '';
    
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const cleanKey = key.toUpperCase().replace(/[^A-Z]/g, '');
    
    if (cleanKey.length !== 26) {
      // Auto-generate key if not provided or invalid
      const combinedChars = cleanKey + alphabet;
      const uniqueChars: string[] = [];
      const seen = new Set<string>();
      
      for (const char of combinedChars) {
        if (!seen.has(char)) {
          seen.add(char);
          uniqueChars.push(char);
        }
      }
      
      const uniqueKey = uniqueChars.join('').slice(0, 26);
      if (uniqueKey.length < 26) {
        const remaining = alphabet.split('').filter(char => !uniqueKey.includes(char));
        const finalKey = uniqueKey + remaining.join('');
        return substitutionHelper(text, alphabet, finalKey, encrypt);
      }
      return substitutionHelper(text, alphabet, uniqueKey, encrypt);
    }
    
    return substitutionHelper(text, alphabet, cleanKey, encrypt);
  }, []);

  const substitutionHelper = (text: string, from: string, to: string, encrypt: boolean): string => {
    const sourceAlphabet = encrypt ? from : to;
    const targetAlphabet = encrypt ? to : from;
    
    return text.replace(/[a-zA-Z]/g, (char) => {
      const isUpperCase = char === char.toUpperCase();
      const upperChar = char.toUpperCase();
      const index = sourceAlphabet.indexOf(upperChar);
      
      if (index === -1) return char;
      
      const newChar = targetAlphabet[index];
      return isUpperCase ? newChar : newChar.toLowerCase();
    });
  };

  // Base64 encoding/decoding
  const base64Transform = useCallback((text: string, encode: boolean): string => {
    try {
      if (encode) {
        return btoa(text);
      } else {
        return atob(text);
      }
    } catch (error) {
      throw new Error('Invalid Base64 input');
    }
  }, []);

  // Morse code implementation
  const morseCode = useCallback((text: string, encode: boolean): string => {
    const morseMap: { [key: string]: string } = {
      'A': '.-', 'B': '-...', 'C': '-.-.', 'D': '-..', 'E': '.', 'F': '..-.',
      'G': '--.', 'H': '....', 'I': '..', 'J': '.---', 'K': '-.-', 'L': '.-..',
      'M': '--', 'N': '-.', 'O': '---', 'P': '.--.', 'Q': '--.-', 'R': '.-.',
      'S': '...', 'T': '-', 'U': '..-', 'V': '...-', 'W': '.--', 'X': '-..-',
      'Y': '-.--', 'Z': '--..', '0': '-----', '1': '.----', '2': '..---',
      '3': '...--', '4': '....-', '5': '.....', '6': '-....', '7': '--...',
      '8': '---..', '9': '----.', ' ': '/'
    };

    const reverseMorseMap = Object.fromEntries(
      Object.entries(morseMap).map(([key, value]) => [value, key])
    );

    if (encode) {
      return text.toUpperCase().split('').map(char => morseMap[char] || char).join(' ');
    } else {
      return text.split(' ').map(code => reverseMorseMap[code] || code).join('');
    }
  }, []);

  // Main processing function
  const processText = useCallback(async () => {
    if (!inputText.trim()) {
      setOutputText('');
      return;
    }

    setIsProcessing(true);
    
    try {
      let result = '';
      const method = cipherMethods.find(m => m.id === selectedMethod);
      
      if (!method) {
        throw new Error('Invalid cipher method selected');
      }

      // Validate key if required
      if (method.keyRequired && !encryptionKey.trim()) {
        throw new Error(`${method.name} requires a key`);
      }

      // Apply preservation settings to input
      let processedInput = inputText;
      if (!preserveSpaces) {
        processedInput = processedInput.replace(/\s/g, '');
      }
      if (!preservePunctuation) {
        processedInput = processedInput.replace(/[^\w\s]/g, '');
      }

      switch (selectedMethod) {
        case 'caesar':
          const shift = parseInt(encryptionKey) || 0;
          if (shift < 0 || shift > 25) {
            throw new Error('Caesar cipher key must be between 0 and 25');
          }
          result = caesarCipher(processedInput, shift, operation === 'encrypt');
          break;

        case 'atbash':
          result = atbashCipher(processedInput);
          break;

        case 'rot13':
          result = caesarCipher(processedInput, 13, true);
          break;

        case 'vigenere':
          if (!/^[a-zA-Z]+$/.test(encryptionKey)) {
            throw new Error('Vigenère key must contain only letters');
          }
          result = vigenereCipher(processedInput, encryptionKey, operation === 'encrypt');
          break;

        case 'simple_substitution':
          result = substitutionCipher(processedInput, encryptionKey, operation === 'encrypt');
          break;

        case 'base64':
          result = base64Transform(processedInput, operation === 'encrypt');
          break;

        case 'reverse':
          result = processedInput.split('').reverse().join('');
          break;

        case 'morse':
          result = morseCode(processedInput, operation === 'encrypt');
          break;

        default:
          throw new Error('Unknown cipher method');
      }

      // Apply case preservation
      if (!preserveCase && selectedMethod !== 'base64' && selectedMethod !== 'morse') {
        result = operation === 'encrypt' ? result.toUpperCase() : result.toLowerCase();
      }

      setOutputText(result);
      setLastResult({ success: true, message: 'Operation completed successfully' });

      // Add to history
      const historyItem: EncryptionHistory = {
        id: Date.now().toString(),
        method: method.name,
        operation,
        timestamp: new Date(),
        success: true,
        inputLength: inputText.length,
        outputLength: result.length
      };
      setHistory(prev => [historyItem, ...prev.slice(0, 49)]);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setLastResult({ success: false, message: errorMessage });
      setOutputText('');

      // Add failed operation to history
      const historyItem: EncryptionHistory = {
        id: Date.now().toString(),
        method: cipherMethods.find(m => m.id === selectedMethod)?.name || 'Unknown',
        operation,
        timestamp: new Date(),
        success: false,
        inputLength: inputText.length,
        outputLength: 0
      };
      setHistory(prev => [historyItem, ...prev.slice(0, 49)]);
    } finally {
      setIsProcessing(false);
    }
  }, [inputText, selectedMethod, encryptionKey, operation, preserveCase, preserveSpaces, preservePunctuation, caesarCipher, atbashCipher, vigenereCipher, substitutionCipher, base64Transform, morseCode, cipherMethods]);

  // Auto-process when inputs change
  useEffect(() => {
    if (autoProcess) {
      const timer = setTimeout(() => {
        processText();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [inputText, selectedMethod, encryptionKey, operation, autoProcess, processText]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setLastResult({ success: true, message: 'Text copied to clipboard' });
    } catch (error) {
      setLastResult({ success: false, message: 'Failed to copy text' });
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText);
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setEncryptionKey('');
    setOperation('encrypt');
    setSelectedMethod('caesar');
    setLastResult(null);
  };

  const generateSampleText = () => {
    const samples = [
      "Hello World! This is a test message for encryption.",
      "The quick brown fox jumps over the lazy dog.",
      "ATTACK AT DAWN FROM THE NORTH GATE",
      "Cryptography is the art of secret communication.",
      "Lorem ipsum dolor sit amet consectetur adipiscing elit."
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const generateRandomKey = () => {
    const method = cipherMethods.find(m => m.id === selectedMethod);
    if (!method || !method.keyRequired) return;

    if (method.keyType === 'numeric') {
      const randomShift = Math.floor(Math.random() * 26);
      setEncryptionKey(randomShift.toString());
    } else if (method.keyType === 'text') {
      const length = Math.floor(Math.random() * 8) + 5; // 5-12 characters
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      let key = '';
      for (let i = 0; i < length; i++) {
        key += letters[Math.floor(Math.random() * letters.length)];
      }
      setEncryptionKey(key);
    }
  };

  const currentMethod = cipherMethods.find(m => m.id === selectedMethod);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className="text-blue-500" size={28} />
            <div>
              <h1 className="text-3xl font-bold">Text Encrypt & Decrypt</h1>
              <p className="text-sm opacity-60">Multi-cipher encryption and decryption tool</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setActiveModal(activeModal === 'history' ? null : 'history')}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Operation History"
            >
              <History size={20} />
            </button>
            
            <button
              onClick={() => setActiveModal(activeModal === 'info' ? null : 'info')}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Cipher Information"
            >
              <Info size={20} />
            </button>
            
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Advanced Settings"
            >
              <Settings size={20} />
            </button>
            
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Toggle Theme"
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          
          {/* Status Bar */}
          {lastResult && (
            <div className={`p-4 rounded-lg border-l-4 ${
              lastResult.success 
                ? (isDarkMode ? 'bg-green-900/20 border-green-500 text-green-400' : 'bg-green-50 border-green-500 text-green-700')
                : (isDarkMode ? 'bg-red-900/20 border-red-500 text-red-400' : 'bg-red-50 border-red-500 text-red-700')
            }`}>
              <div className="flex items-center space-x-2">
                {lastResult.success ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                <span className="text-sm">{lastResult.message}</span>
              </div>
            </div>
          )}

          {/* Controls */}
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              
              {/* Cipher Method Selection */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium mb-2">Cipher Method</label>
                <select
                  value={selectedMethod}
                  onChange={(e) => setSelectedMethod(e.target.value)}
                  className={`w-full p-3 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}
                >
                  {cipherMethods.map(method => (
                    <option key={method.id} value={method.id}>
                      {method.name} - {method.description}
                    </option>
                  ))}
                </select>
                {currentMethod && (
                  <div className="flex items-center space-x-2 mt-2 text-xs">
                    <span className={`px-2 py-1 rounded ${
                      currentMethod.securityLevel === 'high' ? 'bg-green-500 text-white' :
                      currentMethod.securityLevel === 'medium' ? 'bg-yellow-500 text-white' :
                      'bg-red-500 text-white'
                    }`}>
                      Security: {currentMethod.securityLevel.toUpperCase()}
                    </span>
                    {currentMethod.keyRequired && (
                      <span className="px-2 py-1 rounded bg-blue-500 text-white">
                        Key Required: {currentMethod.keyType}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Operation */}
              <div>
                <label className="block text-sm font-medium mb-2">Operation</label>
                <select
                  value={operation}
                  onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                  className={`w-full p-3 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="encrypt">🔒 Encrypt</option>
                  <option value="decrypt">🔓 Decrypt</option>
                </select>
              </div>

              {/* Auto Process Toggle */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="autoProcess"
                  checked={autoProcess}
                  onChange={(e) => setAutoProcess(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="autoProcess" className="text-sm font-medium cursor-pointer">
                  Auto Process
                </label>
                {!autoProcess && (
                  <button
                    onClick={processText}
                    disabled={isProcessing}
                    className={`ml-2 px-4 py-2 rounded border flex items-center space-x-2 ${
                      isDarkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                    } transition-colors disabled:opacity-50`}
                  >
                    {isProcessing ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Zap size={16} />}
                    <span>Process</span>
                  </button>
                )}
              </div>
            </div>

            {/* Encryption Key */}
            {currentMethod?.keyRequired && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">
                    Encryption Key ({currentMethod.keyType === 'numeric' ? 'Numbers only' : 'Text'})
                  </label>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={generateRandomKey}
                      className={`text-xs px-3 py-1 rounded ${
                        isDarkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'
                      } text-white transition-colors`}
                    >
                      Random
                    </button>
                    <button
                      onClick={() => setShowKey(!showKey)}
                      className={`p-1 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                    >
                      {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <input
                  type={showKey ? 'text' : 'password'}
                  value={encryptionKey}
                  onChange={(e) => {
                    if (currentMethod.keyType === 'numeric') {
                      setEncryptionKey(e.target.value.replace(/[^0-9]/g, ''));
                    } else {
                      setEncryptionKey(e.target.value);
                    }
                  }}
                  placeholder={
                    currentMethod.keyType === 'numeric' 
                      ? 'Enter shift value (0-25)...' 
                      : 'Enter text key...'
                  }
                  className={`w-full p-3 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}
                />
                {currentMethod.keyType === 'numeric' && encryptionKey && (
                  <div className="text-xs opacity-60 mt-1">
                    Shift value: {encryptionKey} (A → {String.fromCharCode(65 + (parseInt(encryptionKey) % 26))})
                  </div>
                )}
              </div>
            )}

            {/* Advanced Options */}
            {showAdvanced && (
              <div className={`p-4 rounded border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'} mb-6`}>
                <h3 className="text-sm font-medium mb-3">Advanced Options</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="preserveCase"
                      checked={preserveCase}
                      onChange={(e) => setPreserveCase(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="preserveCase" className="text-sm cursor-pointer">
                      Preserve Case
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="preserveSpaces"
                      checked={preserveSpaces}
                      onChange={(e) => setPreserveSpaces(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="preserveSpaces" className="text-sm cursor-pointer">
                      Preserve Spaces
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="preservePunctuation"
                      checked={preservePunctuation}
                      onChange={(e) => setPreservePunctuation(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="preservePunctuation" className="text-sm cursor-pointer">
                      Preserve Punctuation
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center space-x-4">
              <button
                onClick={swapTexts}
                className={`px-4 py-2 rounded border flex items-center space-x-2 ${
                  isDarkMode ? 'bg-orange-600 border-orange-500 hover:bg-orange-700 text-white' : 'bg-orange-500 border-orange-400 hover:bg-orange-600 text-white'
                } transition-colors`}
              >
                <Shuffle size={16} />
                <span>Swap</span>
              </button>
              
              <button
                onClick={generateSampleText}
                className={`px-4 py-2 rounded border flex items-center space-x-2 ${
                  isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                } transition-colors`}
              >
                <FileText size={16} />
                <span>Sample</span>
              </button>
              
              <button
                onClick={resetAll}
                className={`px-4 py-2 rounded border flex items-center space-x-2 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                } transition-colors`}
              >
                <RotateCcw size={16} />
                <span>Reset</span>
              </button>
            </div>
          </div>

          {/* Text Areas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Input */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center space-x-2">
                  <Lock className="text-blue-500" size={20} />
                  <span>Input Text</span>
                </h3>
                <div className="text-sm opacity-60">
                  {inputText.length} characters
                </div>
              </div>
              
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Enter text to encrypt or decrypt..."
                className={`w-full h-64 p-4 rounded border resize-none ${
                  isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
              
              <div className="mt-4 flex items-center justify-between">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="file"
                    accept=".txt"
                    onChange={handleFileUpload}
                    className="sr-only"
                  />
                  <Upload size={16} className="mr-2" />
                  <span className="text-sm">Upload File</span>
                </label>
                
                <div className="text-xs opacity-60">
                  Letters: {(inputText.match(/[A-Za-z]/g) || []).length} | 
                  Numbers: {(inputText.match(/[0-9]/g) || []).length} | 
                  Symbols: {inputText.length - (inputText.match(/[A-Za-z0-9\s]/g) || []).length}
                </div>
              </div>
            </div>

            {/* Output */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center space-x-2">
                  <Unlock className="text-green-500" size={20} />
                  <span>Output Text</span>
                </h3>
                <div className="flex items-center space-x-2">
                  <div className="text-sm opacity-60">
                    {outputText.length} characters
                  </div>
                  <button
                    onClick={() => copyToClipboard(outputText)}
                    disabled={!outputText}
                    className={`p-2 rounded transition-colors disabled:opacity-30 ${
                      isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    }`}
                    title="Copy to clipboard"
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    onClick={() => downloadText(outputText, `${selectedMethod}-${operation}.txt`)}
                    disabled={!outputText}
                    className={`p-2 rounded transition-colors disabled:opacity-30 ${
                      isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    }`}
                    title="Download as file"
                  >
                    <Download size={16} />
                  </button>
                </div>
              </div>
              
              <textarea
                value={outputText}
                readOnly
                placeholder="Output will appear here..."
                className={`w-full h-64 p-4 rounded border resize-none ${
                  isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                } cursor-default`}
              />
              
              <div className="mt-4 text-xs opacity-60">
                {outputText && (
                  <>
                    Ratio: {inputText.length > 0 ? Math.round((outputText.length / inputText.length) * 100) : 0}% | 
                    Method: {currentMethod?.name} | 
                    Operation: {operation}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Info Modal */}
      {activeModal === 'info' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center space-x-2">
                <Info className="text-blue-500" />
                <span>Cipher Information</span>
              </h2>
              <button
                onClick={() => setActiveModal(null)}
                className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              {cipherMethods.map(method => (
                <div key={method.id} className={`p-4 rounded border ${
                  method.id === selectedMethod 
                    ? (isDarkMode ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50')
                    : (isDarkMode ? 'border-gray-600' : 'border-gray-200')
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">{method.name}</h3>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        method.securityLevel === 'high' ? 'bg-green-500 text-white' :
                        method.securityLevel === 'medium' ? 'bg-yellow-500 text-white' :
                        'bg-red-500 text-white'
                      }`}>
                        {method.securityLevel.toUpperCase()}
                      </span>
                      {method.keyRequired && (
                        <span className="text-xs px-2 py-1 rounded bg-blue-500 text-white">
                          Key: {method.keyType}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm opacity-80">{method.description}</p>
                  
                  {/* Additional information for each cipher */}
                  <div className="mt-3 text-xs space-y-1">
                    {method.id === 'caesar' && (
                      <>
                        <div><strong>How it works:</strong> Shifts each letter by a fixed number of positions in the alphabet.</div>
                        <div><strong>Example:</strong> With key 3, "HELLO" becomes "KHOOR"</div>
                        <div><strong>Security:</strong> Very weak, easily broken by frequency analysis or brute force.</div>
                      </>
                    )}
                    {method.id === 'atbash' && (
                      <>
                        <div><strong>How it works:</strong> Replaces each letter with its mirror in the alphabet (A↔Z, B↔Y, etc.).</div>
                        <div><strong>Example:</strong> "HELLO" becomes "SVOOL"</div>
                        <div><strong>Security:</strong> Very weak, no key variation possible.</div>
                      </>
                    )}
                    {method.id === 'vigenere' && (
                      <>
                        <div><strong>How it works:</strong> Uses a keyword to create multiple Caesar ciphers.</div>
                        <div><strong>Example:</strong> With key "KEY", each letter uses different shifts.</div>
                        <div><strong>Security:</strong> Much stronger than Caesar, resists frequency analysis.</div>
                      </>
                    )}
                    {method.id === 'base64' && (
                      <>
                        <div><strong>How it works:</strong> Encodes binary data using 64 ASCII characters.</div>
                        <div><strong>Example:</strong> "Hello" becomes "SGVsbG8="</div>
                        <div><strong>Note:</strong> Encoding, not encryption - provides no security.</div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 p-4 rounded bg-orange-100 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700">
              <div className="flex items-center space-x-2 text-orange-800 dark:text-orange-300">
                <AlertCircle size={16} />
                <span className="font-semibold">Security Notice</span>
              </div>
              <p className="text-sm text-orange-700 dark:text-orange-400 mt-1">
                These ciphers are for educational purposes only. Most provide minimal security and should not be used for protecting sensitive information. For real security needs, use modern encryption standards like AES.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {activeModal === 'history' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center space-x-2">
                <History className="text-blue-500" />
                <span>Operation History</span>
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setHistory([])}
                  className={`px-3 py-1 text-sm rounded ${
                    isDarkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'
                  } text-white transition-colors`}
                >
                  Clear
                </button>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} text-center`}>
                <div className="text-2xl font-bold text-blue-500">{history.length}</div>
                <div className="text-sm">Total Operations</div>
              </div>
              <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} text-center`}>
                <div className="text-2xl font-bold text-green-500">
                  {history.filter(h => h.success).length}
                </div>
                <div className="text-sm">Successful</div>
              </div>
              <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} text-center`}>
                <div className="text-2xl font-bold text-orange-500">
                  {history.filter(h => h.operation === 'encrypt').length}
                </div>
                <div className="text-sm">Encryptions</div>
              </div>
              <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} text-center`}>
                <div className="text-2xl font-bold text-purple-500">
                  {history.filter(h => h.operation === 'decrypt').length}
                </div>
                <div className="text-sm">Decryptions</div>
              </div>
            </div>
            
            {/* History List */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {history.length > 0 ? history.map(item => (
                <div key={item.id} className={`p-4 rounded border ${
                  isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        item.success 
                          ? 'bg-green-500 text-white' 
                          : 'bg-red-500 text-white'
                      }`}>
                        {item.success ? 'SUCCESS' : 'FAILED'}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        item.operation === 'encrypt' 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-purple-500 text-white'
                      }`}>
                        {item.operation.toUpperCase()}
                      </span>
                      <span className="text-xs px-2 py-1 rounded bg-gray-500 text-white">
                        {item.method}
                      </span>
                    </div>
                    <span className="text-xs opacity-60">
                      {item.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="text-sm">
                    <div>Input: {item.inputLength} chars → Output: {item.outputLength} chars</div>
                    {item.success && (
                      <div className="opacity-60">
                        Ratio: {item.inputLength > 0 ? Math.round((item.outputLength / item.inputLength) * 100) : 0}%
                      </div>
                    )}
                  </div>
                </div>
              )) : (
                <div className="text-center py-8 opacity-60">
                  <History size={32} className="mx-auto mb-2" />
                  <div>No operations performed yet</div>
                  <div className="text-xs">Start encrypting or decrypting to see history</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TextEncryptDecryptApp;